import { useState } from "react";

import clsx from "clsx";
import moment from "moment";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useFilters } from "@features/breakdown-v2/breakdowns/hooks";
import {
  DELETE_LABEL_RULE_GROUP_V3,
  DUPLICATE_LABEL_RULE_GROUP_V3,
  GET_LABEL_RULE_GROUPS_V3,
  GET_LABEL_WIZARD_DATA_V3,
  PATCH_LABEL_RULE_GROUP_V3,
} from "@gql/label-rules-v3";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type DeleteLabelRuleGroupV3,
  type DeleteLabelRuleGroupV3Variables,
  type DuplicateLabelRuleGroupV3,
  type DuplicateLabelRuleGroupV3Variables,
  type GetLabelRuleGroupsV3_me_labelRuleGroupsV3,
  type PatchLabelRuleGroupV3,
  type PatchLabelRuleGroupV3Variables,
} from "@north-beam/nb-client-side";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@shared/buttons";
import { notifyLabelsCache } from "@shared/notify-labels-cache";
import Spinner from "@shared/spinner";
import { Switch } from "@shared/switch";

interface Props {
  breakdown: GetLabelRuleGroupsV3_me_labelRuleGroupsV3;
}

export const BreakdownItem = ({ breakdown }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { filters } = useFilters();
  const { addToast } = useToasts();

  const [aboutToDelete, setAboutToDelete] = useState(false);

  const [patchLabelRuleGroup, { loading: patching }] = useNorthbeamMutation<
    PatchLabelRuleGroupV3,
    PatchLabelRuleGroupV3Variables
  >(PATCH_LABEL_RULE_GROUP_V3, {
    useCpuIntensive: true,
    refetchQueries: [{ query: GET_LABEL_WIZARD_DATA_V3 }],
    awaitRefetchQueries: true,
  });

  const [duplicateLabelRuleGroup, { loading: duplicating }] =
    useNorthbeamMutation<
      DuplicateLabelRuleGroupV3,
      DuplicateLabelRuleGroupV3Variables
    >(DUPLICATE_LABEL_RULE_GROUP_V3, {
      useCpuIntensive: true,
      refetchQueries: [
        { query: GET_LABEL_RULE_GROUPS_V3 },
        { query: GET_LABEL_WIZARD_DATA_V3 },
      ],
      awaitRefetchQueries: true,
    });

  const [deleteLabelRuleGroup, { loading: deleting }] = useNorthbeamMutation<
    DeleteLabelRuleGroupV3,
    DeleteLabelRuleGroupV3Variables
  >(DELETE_LABEL_RULE_GROUP_V3, {
    useCpuIntensive: true,
    refetchQueries: [
      { query: GET_LABEL_RULE_GROUPS_V3 },
      { query: GET_LABEL_WIZARD_DATA_V3 },
    ],
    awaitRefetchQueries: true,
  });

  const first3Segments = breakdown.segments.slice(0, 3);
  const restSegments = breakdown.segments.slice(3);

  const params = new URLSearchParams({
    search: filters.search,
    sorting: filters.sorting,
  });

  return (
    <div
      className={clsx(
        "p-2 border rounded-md flex items-center justify-between gap-6 group/item",
        {
          "border-gray-2 hover:border-gray+1": !aboutToDelete,
          "border-mars-2 bg-mars-2": aboutToDelete,
        },
      )}
    >
      {aboutToDelete ? (
        <>
          <div className="font-bold">Delete breakdown?</div>
          <div className="py-[7px] flex items-center gap-2">
            <PrimaryButton
              disabled={deleting}
              className={clsx("font-bold flex items-center gap-2", {
                "bg-mars hover:bg-mars+2": !deleting,
              })}
              onClick={async () => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo: "Delete breakdown",
                  data: {
                    pageContext: { id: breakdown.id },
                  },
                });

                await deleteLabelRuleGroup({ variables: { id: breakdown.id } });
                notifyLabelsCache();
              }}
            >
              {deleting && <Spinner colorClassname="text-white-400" />}
              YES, DELETE "{breakdown.name}" BREAKDOWN
            </PrimaryButton>
            <SecondaryButton
              disabled={deleting}
              className="font-bold"
              onClick={() => setAboutToDelete(false)}
            >
              CANCEL
            </SecondaryButton>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-1 items-center gap-2 text-lg truncate">
            <div>
              {patching ? (
                <img
                  src={salesPlatformColumnLoading}
                  alt="Loading table"
                  style={{ height: "1.5rem" }}
                />
              ) : (
                <Switch
                  id={breakdown.id}
                  size="small"
                  value={breakdown.active}
                  onChange={(active) => {
                    prettyLogEventPage({
                      domain: "Breakdown v2",
                      action: "onChange",
                      additionalActionInfo: "Change breakdown active",
                      data: {
                        pageContext: { id: breakdown.id },
                      },
                    });

                    patchLabelRuleGroup({
                      variables: { id: breakdown.id, active },
                    });
                  }}
                />
              )}
            </div>
            <div className="truncate">
              <GenericTooltip noInfoCircle content={breakdown.name}>
                {breakdown.name}
              </GenericTooltip>
            </div>
          </div>
          <div className="flex flex-1 items-stretch gap-6">
            <GenericTooltip
              className={clsx(
                "shrink-0 w-[240px] text-xs inline-flex flex-col justify-center gap-1",
                "hover:bg-cosmic-4 rounded-md py-2 px-3 max-w-[100%]",
              )}
              noInfoCircle
              mouseEnterDelay={0.3}
              placement="bottom"
              content={
                <div className="text-xs min-w-[100px] space-y-1">
                  {breakdown.segments.map((e) => (
                    <div key={e}>{e}</div>
                  ))}
                </div>
              }
            >
              <div className="flex items-center text-xxs gap-1">
                <i className="fa-regular fa-list text-gray text-xxs" />
                Segments
              </div>
              <div className="flex whitespace-nowrap">
                <div className="truncate">{first3Segments.join(", ")}</div>
                {restSegments.length > 0 && (
                  <span className="text-cosmic font-bold">
                    , +{restSegments.length} more
                  </span>
                )}
              </div>
            </GenericTooltip>
            <div className="text-xs shrink-0 flex flex-col justify-center w-[180px]">
              <div className="flex items-center text-xxs gap-1 mb-1">
                <i className="fa-regular fa-clock text-gray text-xxs" />
                Last update
              </div>
              <div className="text-cosmic font-bold mb-0.5">
                {moment(breakdown.updatedAt).format("MMM DD, YYYY @ h:mm A")}
              </div>
              <div className="font-bold">{breakdown.updatedBy}</div>
            </div>
            <div
              className={clsx(
                "text-xxs flex items-center gap-1.5 bg-interstellar-3 rounded-md px-2 py-1",
                "shrink-0 w-[120px]",
                { invisible: breakdown.isUsingSimpleUI },
              )}
            >
              <i className="fa-regular fa-code text-gray+1" />
              Created with advanced rules
            </div>
          </div>
          <div
            className={clsx(
              "flex items-center justify-end gap-2 invisible group-hover/item:visible",
              "shrink-0 w-[250px]",
            )}
          >
            <TertiaryButton
              disabled={duplicating}
              className="flex items-center gap-2 font-bold bg-cosmic-4 hover:bg-cosmic-3"
              onClick={async () => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo: "Duplicate breakdown",
                  data: {
                    pageContext: { id: breakdown.id },
                  },
                });

                const result = await duplicateLabelRuleGroup({
                  variables: {
                    id: breakdown.id,
                    isUsingSimpleUI: breakdown.isUsingSimpleUI,
                  },
                });

                addToast(
                  `Breakdown "${result.data?.duplicateLabelRuleGroupV3.name}" created`,
                  {
                    appearance: "success",
                  },
                );
              }}
            >
              {duplicating ? (
                <Spinner colorClassname="text-white-400" />
              ) : (
                <i className="fa-regular fa-copy" />
              )}
              DUPLICATE
            </TertiaryButton>
            <Link
              className={clsx(
                "button tertiary-button sm inline-block no-underline font-bold",
                "flex items-center gap-2 bg-cosmic-4 hover:bg-cosmic-3",
              )}
              to={`../breakdowns/${breakdown.id}/edit?${params.toString()}`}
              relative="path"
            >
              <i className="fa-regular fa-pen" /> EDIT
            </Link>
            <TertiaryButton
              className="flex items-center gap-2 font-bold w-8 h-8 px-[11px] bg-mars-2 hover:bg-[#fce2db]"
              onClick={() => setAboutToDelete(true)}
            >
              <i className="fa-regular fa-trash text-mars" />
            </TertiaryButton>
          </div>
        </>
      )}
    </div>
  );
};
